/**
 * Phase 45 Plan 45-05 — ManagerOverviewPage (Variant B Hybrid).
 *
 * Top-level page composing:
 *   ManagerOverviewHeader        — eyebrow + h1 + Export CSV + + New project
 *   ManagerOverviewKpiBand       — 4× KpiCard
 *   ManagerOverviewFilterRow     — 5× FilterChip (read-only) + GroupByToggle
 *   NeedsAttentionRail           — top 3 of needsAttention (server pre-sorts)
 *   PortfolioTable               — sticky header + group rollups + rows
 *
 * Loading / error / empty per UI-SPEC §Surface 1 "Empty/loading/error".
 *
 * Layout: bg-ms-canvas flex flex-col h-full; header band px-7 pt-6 pb-5;
 * needs-attention rail px-7 pb-4; table region px-7 pb-5 flex-1.
 *
 * Route registration: /manager/overview under <ManagerRoute> in App.tsx
 * (CONTEXT D-09 + ProtectedRoute.tsx:40 requiredRoles ['admin', 'manager']).
 *
 * MKS-464 — the page carries a "Portfolio | Delivery" tab switch (selected tab in `?tab=`).
 * The portfolio content is the unchanged `PortfolioView` below.
 */
import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ManagerOverviewHeader } from '../../components/manager-overview/ManagerOverviewHeader';
import { ManagerOverviewKpiBand } from '../../components/manager-overview/ManagerOverviewKpiBand';
import { ManagerOverviewFilterRow } from '../../components/manager-overview/ManagerOverviewFilterRow';
import { NeedsAttentionRail } from '../../components/manager-overview/NeedsAttentionRail';
import { TierSummaryBand } from '../../components/manager-overview/TierSummaryBand';
import { PortfolioTable } from '../../components/manager-overview/PortfolioTable';
import type { GroupBy } from '../../components/manager-overview/GroupByToggle';
import { DeliveryTab } from '../../components/manager-overview/DeliveryTab';
import { Btn } from '../../components/ui/Btn';
import { PlusIcon } from '../../components/ui/icons';
import {
  usePortfolioOverview,
  type PortfolioPeriod,
} from '../../hooks/usePortfolioOverview';

type OverviewTab = 'portfolio' | 'delivery';

const TABS: Array<{ value: OverviewTab; label: string }> = [
  { value: 'portfolio', label: 'Portfolio' },
  { value: 'delivery', label: 'Delivery' },
];

export function ManagerOverviewPage() {
  const [searchParams, setSearchParams] = useSearchParams();

  // P-15: an invalid value reads as the default; writing the default removes the param.
  const tab: OverviewTab = searchParams.get('tab') === 'delivery' ? 'delivery' : 'portfolio';

  function selectTab(next: OverviewTab) {
    const params = new URLSearchParams(searchParams);
    if (next === 'portfolio') params.delete('tab');
    else params.set('tab', next);
    setSearchParams(params, { replace: true });
  }

  return (
    <div className="bg-ms-canvas flex flex-col min-h-full">
      <div className="px-7 pt-4">
        <div
          className="inline-flex bg-ms-surface-2 rounded-md p-0.5 border border-ms-border-subtle"
          role="tablist"
          aria-label="Manager overview view"
        >
          {TABS.map((opt) => {
            const active = opt.value === tab;
            const cls = active
              ? 'bg-ms-surface text-ms-text-strong shadow-sm'
              : 'text-ms-text-secondary hover:text-ms-text-strong';
            return (
              <button
                key={opt.value}
                type="button"
                role="tab"
                aria-selected={active}
                className={`px-2.5 py-1 rounded text-[11.5px] font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-ms-orange/40 ${cls}`}
                onClick={() => selectTab(opt.value)}
              >
                {opt.label}
              </button>
            );
          })}
        </div>
      </div>
      {tab === 'delivery' ? <DeliveryTab /> : <PortfolioView />}
    </div>
  );
}

function PortfolioView() {
  const [period] = useState<PortfolioPeriod>('this_quarter');
  const [groupBy, setGroupBy] = useState<GroupBy>('customer');

  const { data, isLoading, isError, refetch } = usePortfolioOverview(period);

  // Resolved period info (falls back to a static label while loading so the
  // header doesn't flicker blank between query revalidations).
  const periodInfo =
    data?.period ?? { from: '', to: '', label: 'This quarter' };

  return (
    <div className="bg-ms-canvas flex flex-col min-h-full">
      <header className="px-7 pt-6 pb-5 border-b border-ms-border-subtle">
        <div className="mb-5">
          <ManagerOverviewHeader period={periodInfo} />
        </div>
        {isLoading ? (
          <KpiBandSkeleton />
        ) : data ? (
          <ManagerOverviewKpiBand kpis={data.kpis} projects={data.projects} />
        ) : (
          <KpiBandSkeleton />
        )}
        {data && (
          <ManagerOverviewFilterRow
            projects={data.projects}
            period={data.period}
            groupBy={groupBy}
            onGroupByChange={setGroupBy}
          />
        )}
      </header>

      {/* Body */}
      {isLoading && (
        <>
          <div className="px-7 pb-4">
            <RailSkeleton />
          </div>
          <div className="px-7 pb-5 flex-1">
            <TableSkeleton />
          </div>
        </>
      )}

      {isError && !isLoading && (
        <div className="px-7 py-12 flex-1 flex items-start justify-center">
          <div className="bg-ms-surface border border-ms-border-subtle rounded-lg p-6 max-w-md w-full text-center">
            <div className="text-[12.5px] text-rose-600 mb-3">
              Couldn&apos;t load portfolio data. Try again.
            </div>
            <Btn variant="outline-accent" size="sm" type="button" onClick={() => refetch()}>
              Retry
            </Btn>
          </div>
        </div>
      )}

      {data && !isLoading && !isError && data.projects.length === 0 && (
        <div className="px-7 py-12 flex-1 flex items-start justify-center">
          <div className="bg-ms-surface border border-ms-border-subtle rounded-lg p-8 max-w-md w-full text-center">
            <p className="text-[12.5px] text-ms-text-secondary mb-4">
              No active projects yet. Create a project to populate this overview.
            </p>
            <Link
              to="/projects/new"
              className="inline-flex items-center justify-center gap-1.5 bg-ms-orange-dark text-white text-[11.5px] font-medium px-2.5 py-1.5 rounded-md hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-ms-orange/40 focus:ring-offset-1"
            >
              <PlusIcon className="w-3.5 h-3.5" aria-hidden="true" />
              New project
            </Link>
          </div>
        </div>
      )}

      {data && !isLoading && !isError && data.projects.length > 0 && (
        <>
          {data.needsAttention.length > 0 && (
            <div className="px-7 pb-4">
              <NeedsAttentionRail items={data.needsAttention} />
            </div>
          )}
          {/* Plan 50-07 — Tier summary band above PortfolioTable */}
          {(data.tierRollup ?? []).length > 0 && (
            <div className="px-7 pb-2">
              <TierSummaryBand tierRollup={data.tierRollup ?? []} />
            </div>
          )}
          <div className="px-7 pb-5 flex-1 overflow-auto">
            <PortfolioTable rows={data.projects} groupBy={groupBy} />
          </div>
        </>
      )}
    </div>
  );
}

function KpiBandSkeleton() {
  return (
    <div className="grid grid-cols-4 gap-3 mb-5" aria-hidden="true">
      {[0, 1, 2, 3].map((i) => (
        <div key={i} className="bg-ms-surface-2 rounded-lg h-[88px] animate-pulse" />
      ))}
    </div>
  );
}

function RailSkeleton() {
  return (
    <div className="grid grid-cols-3 gap-3" aria-hidden="true">
      {[0, 1, 2].map((i) => (
        <div key={i} className="bg-ms-surface-2 rounded-lg h-[112px] animate-pulse" />
      ))}
    </div>
  );
}

function TableSkeleton() {
  return (
    <div
      className="bg-ms-surface border border-ms-border-subtle rounded-lg overflow-hidden"
      aria-hidden="true"
    >
      {Array.from({ length: 8 }).map((_, i) => (
        <div key={i} className="h-[44px] border-b border-ms-border-subtle animate-pulse bg-ms-surface-2/30" />
      ))}
    </div>
  );
}

export default ManagerOverviewPage;
